import React, { useState, useEffect, useCallback } from 'react';

// 예매금액설정 (2026-07-26 신설)
// 대행원가(씨네체인 시세관리)에 수식을 적용해 고객 판매가를 정한다. 실무자가 기준가·마진%·수식을 편집.
// 서버 API: /request/getPricePolicy, /savePricePolicy(수식 서버검증), /pricePreview(미리보기), /priceLedger(월/일/건별)

const DEFAULT_VARS = { '기준가': 15000, '마진율': 0.24, '영업율': 0.06, 'PG율': 0.033 };
const DEFAULT_FORMULAS = {
  sale_formula: '원가 + (기준가 - 원가) * 마진율',
  commission_formula: '(기준가 - 원가) * 영업율',
  pg_formula: '원가 * PG율',
  profit_formula: '판매가 - 원가 - 영업수당 - PG',
};
const won = (n) => (n == null || isNaN(n)) ? '-' : Number(n).toLocaleString('ko-KR') + '원';

export default function PriceSettingScreen() {
  const [formulas, setFormulas] = useState(DEFAULT_FORMULAS);
  const [vars, setVars] = useState(DEFAULT_VARS);
  const [maxMul, setMaxMul] = useState(3);
  const [preview, setPreview] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [activePolicy, setActivePolicy] = useState(null); // 현재 적용 중인 정책(없으면 원가=판매가)
  const [latestCost, setLatestCost] = useState({ CGV: 0, '롯데시네마': 0, '메가박스': 0 }); // 오늘 시세(원가)
  const [brand, setBrand] = useState('CGV'); // 계산기 브랜드 탭
  // 실적 조회
  const [gran, setGran] = useState('day');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [ledger, setLedger] = useState([]);

  // 활성 정책 로드 (+ 현재 적용 상태 표시용)
  const loadPolicy = () => {
    fetch('/request/getPricePolicy', { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        const p = (d.policies || []).find(x => x.brand == null) || (d.policies || [])[0];
        setActivePolicy(p || null);
        if (p) {
          setFormulas({
            sale_formula: p.sale_formula || DEFAULT_FORMULAS.sale_formula,
            commission_formula: p.commission_formula || DEFAULT_FORMULAS.commission_formula,
            pg_formula: p.pg_formula || DEFAULT_FORMULAS.pg_formula,
            profit_formula: p.profit_formula || DEFAULT_FORMULAS.profit_formula,
          });
          if (p.variables) setVars(p.variables);
          if (p.max_multiplier) setMaxMul(Number(p.max_multiplier));
        }
      })
      .catch(() => {});
  };
  useEffect(() => { loadPolicy(); }, []);

  // 오늘 시세(원가) 로드 — 계산기 브랜드 탭 기본값
  // [장실장님 MAX 2026-08-08] "오늘 대행원가"는 영화거래소로 실제 넘어가는 값 = costEff(브랜드 vs 장실장님 MAX).
  //   원본 cost(9888)가 아니라 costEff(10000)를 써야 사장님이 보는 값과 판매가 계산이 일치한다.
  useEffect(() => {
    fetch('/request/priceHistory', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.ok && d.rows && d.rows[0]) setLatestCost(d.rows[0].costEff || d.rows[0].cost); })
      .catch(() => {});
  }, []);

  // 미리보기 (수식/변수 바뀔 때마다)
  const refreshPreview = useCallback(() => {
    fetch('/request/pricePreview', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...formulas, variables: vars, max_multiplier: maxMul }),
    })
      .then(r => r.json())
      .then(d => setPreview(d.ok ? d.rows : []))
      .catch(() => setPreview([]));
  }, [formulas, vars, maxMul]);

  useEffect(() => { const t = setTimeout(refreshPreview, 300); return () => clearTimeout(t); }, [refreshPreview]);

  const setVar = (k, v) => setVars(prev => ({ ...prev, [k]: v === '' ? '' : Number(v) }));
  const setFormula = (k, v) => setFormulas(prev => ({ ...prev, [k]: v }));

  // 계산기: 기본 수식(원가+(기준가-원가)*마진율 등) 기준 즉시 계산. 변수 슬라이더로 조절.
  const calc = (cost) => {
    const base = Number(vars['기준가']) || 0, m = Number(vars['마진율']) || 0, s = Number(vars['영업율']) || 0, p = Number(vars['PG율']) || 0;
    const sale = cost + (base - cost) * m;
    const commission = (base - cost) * s;
    const pg = cost * p;
    const profit = sale - cost - commission - pg;
    return { sale: Math.round(sale), commission: Math.round(commission), pg: Math.round(pg), profit: Math.round(profit), rate: sale > 0 ? (profit / sale * 100) : 0 };
  };
  const curCost = Number(latestCost[brand]) || 0;
  const cur = calc(curCost);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await fetch('/request/savePricePolicy', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand: null, ...formulas, variables: vars, max_multiplier: maxMul }),
      });
      const d = await res.json();
      if (d.ok) { setMsg('✅ 저장했습니다. 지금부터 홈페이지 판매가에 적용됩니다.'); loadPolicy(); }
      else setMsg('❌ 저장 실패: ' + (d.error || '알 수 없는 오류'));
    } catch (e) { setMsg('❌ 저장 요청 실패: ' + e.message); }
    finally { setSaving(false); }
  };

  const loadLedger = async () => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    qs.set('granularity', gran);
    try {
      const d = await (await fetch('/request/priceLedger?' + qs.toString(), { credentials: 'include' })).json();
      setLedger(d.ok ? d.rows : []);
    } catch (e) { setLedger([]); }
  };

  const inputStyle = { width: '100%', padding: '7px 9px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
  const th = { textAlign: 'right', padding: '8px 10px', fontSize: 12, color: '#666', borderBottom: '2px solid #e5e5e5', whiteSpace: 'nowrap' };
  const td = { textAlign: 'right', padding: '7px 10px', borderBottom: '1px solid #eee', fontVariantNumeric: 'tabular-nums' };

  return (
    <div style={{ padding: 24, width: '100%', maxWidth: 1100 }}>
      <h2 style={{ margin: '0 0 4px' }}>예매금액설정</h2>
      <p style={{ color: '#777', fontSize: 13, margin: '0 0 14px' }}>
        씨네체인 <b>대행원가 시세관리</b>에 입력된 원가에 아래 수식을 적용해 고객 판매가를 정합니다.
        변수(기준가·마진율 등)와 수식을 엑셀처럼 편집할 수 있고, 저장하면 즉시 영화거래소 홈페이지 판매가에 반영됩니다.
      </p>

      {/* 현재 적용 상태 배너 — 정책 있으면 마진 적용중, 없으면 원가=판매가 안내 */}
      {activePolicy ? (
        <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 14 }}>
          <b style={{ color: '#2e7d32' }}>✅ 현재 판매가에 마진이 적용되고 있습니다.</b>
          <span style={{ color: '#555', marginLeft: 8 }}>
            마진율 <b>{Math.round((Number(activePolicy.variables?.['마진율']) || 0) * 100)}%</b>,
            기준가 <b>{Number(activePolicy.variables?.['기준가'] || 0).toLocaleString()}원</b>
            {activePolicy.effective_from ? ` · ${String(activePolicy.effective_from).slice(0, 10)}부터 적용` : ''}
          </span>
        </div>
      ) : (
        <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 14 }}>
          <b style={{ color: '#e65100' }}>⚠ 아직 마진 정책이 설정되지 않았습니다.</b>
          <span style={{ color: '#555', marginLeft: 8 }}>지금은 <b>대행원가가 그대로 판매가</b>로 나갑니다. 아래에서 마진을 정하고 <b>저장</b>하면 판매가에 반영됩니다.</span>
        </div>
      )}

      {/* ===== 계산기: 브랜드 탭 + 현재시세 요약 + 슬라이더 ===== */}
      <div style={{ border: '1px solid #e5e5e5', borderRadius: 12, padding: 18, marginBottom: 22, background: 'linear-gradient(#fafbff,#fff)' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {['CGV', '롯데시네마', '메가박스'].map(b => (
            <button key={b} onClick={() => setBrand(b)}
              style={{ flex: 1, padding: '9px 4px', borderRadius: 9, border: '1px solid ' + (brand === b ? '#f0b544' : '#ddd'), background: brand === b ? '#f0b544' : '#f7f8fc', color: brand === b ? '#1a1400' : '#666', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>{b}</button>
          ))}
        </div>

        {/* 현재 시세(원가) 요약 카드 4개 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 18 }}>
          {[
            ['오늘 대행원가', won(curCost), '#555'],
            ['고객 판매가', won(cur.sale), '#c98a12'],
            ['건당 순이익', won(cur.profit), cur.profit < 300 ? '#dc2626' : '#16a34a'],
            ['순이익률', cur.rate.toFixed(1) + '%', '#1976d2'],
          ].map(([k, v, color], i) => (
            <div key={i} style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '11px 13px' }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{k}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
            </div>
          ))}
        </div>

        {/* 슬라이더: 기준가·마진율·영업율·PG율 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 14 }}>
          {[
            ['기준가', vars['기준가'], 5000, 30000, 500, '원', 1],
            ['마진율', vars['마진율'], 0, 0.6, 0.01, '%', 100],
            ['영업율', vars['영업율'], 0, 0.3, 0.005, '%', 100],
            ['PG율', vars['PG율'], 0, 0.1, 0.001, '%', 100],
          ].map(([k, val, min, max, step, unit, mul]) => (
            <div key={k}>
              <label style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#555', marginBottom: 4 }}>
                <span>{k}</span>
                <b>{unit === '%' ? (Number(val) * mul).toFixed(unit === '%' && k !== '기준가' ? 1 : 0) : Number(val).toLocaleString()}{unit}</b>
              </label>
              <input type="range" min={min} max={max} step={step} value={val || 0}
                onChange={e => setVar(k, e.target.value)} style={{ width: '100%', accentColor: '#1976d2' }} />
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, color: '#999', marginTop: 12 }}>
          슬라이더를 움직이면 위 요약과 아래 표가 즉시 바뀝니다. 원하는 값이 되면 아래 <b>저장</b>을 눌러 홈페이지에 반영하세요.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {/* 변수 + 수식 편집 */}
        <div style={{ flex: '1 1 360px', minWidth: 320, border: '1px solid #e5e5e5', borderRadius: 10, padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#555', marginBottom: 12 }}>계산 변수</div>
          {Object.keys(vars).map(k => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <label style={{ width: 90, fontSize: 13, color: '#444' }}>{k}</label>
              <input type="number" step="any" value={vars[k]} onChange={e => setVar(k, e.target.value)} style={inputStyle} />
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <label style={{ width: 90, fontSize: 13, color: '#444' }}>상한배수</label>
            <input type="number" step="0.1" value={maxMul} onChange={e => setMaxMul(Number(e.target.value))} style={inputStyle} />
          </div>
          <div style={{ fontSize: 11, color: '#999', marginBottom: 16 }}>* 상한배수: 판매가가 원가×배수 초과 시 오타로 보고 안전 폴백</div>

          <div style={{ fontWeight: 700, fontSize: 13, color: '#555', marginBottom: 10 }}>수식 (변수·사칙연산·괄호만)</div>
          {[
            ['sale_formula', '판매가'],
            ['commission_formula', '영업수당'],
            ['pg_formula', 'PG'],
            ['profit_formula', '순이익'],
          ].map(([k, label]) => (
            <div key={k} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 3 }}>{label}</div>
              <input value={formulas[k]} onChange={e => setFormula(k, e.target.value)} style={{ ...inputStyle, fontFamily: 'monospace' }} />
            </div>
          ))}
          <div style={{ fontSize: 11, color: '#999', margin: '4px 0 14px' }}>
            사용 가능 변수: 원가, {Object.keys(vars).join(', ')}, (순이익식엔) 판매가·영업수당·PG
          </div>

          <button onClick={save} disabled={saving}
            style={{ width: '100%', padding: '11px', background: '#1976d2', color: '#fff', border: 0, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            {saving ? '저장 중…' : '저장 (홈페이지 즉시 반영)'}
          </button>
          {msg && <div style={{ marginTop: 10, fontSize: 13, color: msg.startsWith('✅') ? '#16a34a' : '#dc2626' }}>{msg}</div>}
        </div>

        {/* 미리보기 표 */}
        <div style={{ flex: '2 1 480px', minWidth: 380, border: '1px solid #e5e5e5', borderRadius: 10, padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#555', marginBottom: 12 }}>원가별 수익 미리보기</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>
                <th style={{ ...th, textAlign: 'left' }}>대행원가</th>
                <th style={th}>판매가</th><th style={th}>영업수당</th><th style={th}>PG사</th><th style={th}>순이익</th>
              </tr></thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={i}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{won(r.cost)}</td>
                    <td style={{ ...td, color: '#c98a12', fontWeight: 700 }}>{won(r.sale)}</td>
                    <td style={td}>{won(r.commission)}</td>
                    <td style={td}>{won(r.pg)}</td>
                    <td style={{ ...td, color: r.profit < 300 ? '#dc2626' : '#16a34a', fontWeight: 700 }}>{won(r.profit)}{r.fallback ? ' ⚠' : ''}</td>
                  </tr>
                ))}
                {preview.length === 0 && <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#999' }}>수식을 확인하세요</td></tr>}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>⚠ = 수식 오류/상한초과로 원가=판매가 폴백된 행</div>
        </div>
      </div>

      {/* 월/일/건별 실적 */}
      <div style={{ marginTop: 24, border: '1px solid #e5e5e5', borderRadius: 10, padding: 18 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#555', marginBottom: 12 }}>판매 실적 (월별 / 일자별 / 건별)</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
          {['month', 'day', 'order'].map(g => (
            <button key={g} onClick={() => setGran(g)}
              style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ccc', background: gran === g ? '#1976d2' : '#fff', color: gran === g ? '#fff' : '#555', fontSize: 13, cursor: 'pointer' }}>
              {g === 'month' ? '월별' : g === 'day' ? '일자별' : '건별'}
            </button>
          ))}
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ padding: 6, borderRadius: 6, border: '1px solid #ccc' }} />
          <span style={{ color: '#999' }}>~</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ padding: 6, borderRadius: 6, border: '1px solid #ccc' }} />
          <button onClick={loadLedger} style={{ padding: '6px 16px', borderRadius: 6, border: 0, background: '#333', color: '#fff', fontSize: 13, cursor: 'pointer' }}>조회</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              {gran === 'order' ? (
                <tr><th style={{ ...th, textAlign: 'left' }}>일시</th><th style={th}>주문</th><th style={th}>브랜드</th><th style={th}>추천인</th><th style={th}>원가</th><th style={th}>판매가</th><th style={th}>영업수당</th><th style={th}>PG</th><th style={th}>순이익</th></tr>
              ) : (
                <tr><th style={{ ...th, textAlign: 'left' }}>{gran === 'month' ? '월' : '일자'}</th><th style={th}>건수</th><th style={th}>판매합</th><th style={th}>원가합</th><th style={th}>영업수당합</th><th style={th}>PG합</th><th style={th}>순이익합</th></tr>
              )}
            </thead>
            <tbody>
              {ledger.map((r, i) => gran === 'order' ? (
                <tr key={i}>
                  <td style={{ ...td, textAlign: 'left' }}>{r.bucket}</td><td style={td}>{r.id}</td><td style={td}>{r.cinema_brand}</td><td style={td}>{r.referral_code || '-'}</td>
                  <td style={td}>{won(r.cost_amount)}</td><td style={td}>{won(r.sale_unit_price)}</td><td style={td}>{won(r.commission_amount)}</td><td style={td}>{won(r.pg_amount)}</td><td style={td}>{won(r.net_profit)}</td>
                </tr>
              ) : (
                <tr key={i}>
                  <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{r.bucket}</td><td style={td}>{r.cnt}</td>
                  <td style={td}>{won(r.sale_sum)}</td><td style={td}>{won(r.cost_sum)}</td><td style={td}>{won(r.commission_sum)}</td><td style={td}>{won(r.pg_sum)}</td><td style={{ ...td, fontWeight: 700 }}>{won(r.profit_sum)}</td>
                </tr>
              ))}
              {ledger.length === 0 && <tr><td colSpan={9} style={{ padding: 20, textAlign: 'center', color: '#999' }}>조회 버튼을 눌러 실적을 확인하세요 (주문에 스냅샷이 쌓이면 표시)</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
